import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft, RefreshCw, CheckCircle, XCircle, Clock, DollarSign, Activity } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

const AdminStripeEvents = ({ user }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({ total_events: 0, successful_payments: 0, failed_payments: 0 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('');

  const getAdminId = useCallback(() => {
    if (user?.id) return user.id;
    try {
      const stored = localStorage.getItem('vowflow_user');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.id;
      }
    } catch (e) {
      console.error('Error getting admin ID:', e);
    }
    return null;
  }, [user]);

  const loadEvents = useCallback(async () => {
    const adminId = getAdminId();
    if (!adminId) {
      toast.error('Admin authentication required');
      navigate('/admin/login');
      return;
    }

    try {
      const [eventsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/admin/stripe-events`, {
          headers: { 'admin-id': adminId },
          params: { limit: 100, event_type: filter || undefined }
        }),
        axios.get(`${API}/admin/stripe-events/summary`, {
          headers: { 'admin-id': adminId }
        })
      ]);

      setEvents(eventsRes.data.events || []);
      setStats(eventsRes.data.stats || {});
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading Stripe events:', error);
      toast.error('Failed to load Stripe events');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAdminId, filter, navigate]);

  useEffect(() => {
    loadEvents();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadEvents, 30000);
    return () => clearInterval(interval);
  }, [loadEvents]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadEvents();
  };

  const getEventIcon = (eventType) => {
    if (eventType.includes('completed') || eventType.includes('succeeded')) {
      return <CheckCircle size={16} className="text-green-500" />;
    }
    if (eventType.includes('failed') || eventType.includes('canceled')) {
      return <XCircle size={16} className="text-red-500" />;
    }
    return <Clock size={16} className="text-yellow-500" />;
  };

  const getEventBadgeColor = (eventType) => {
    if (eventType.includes('completed') || eventType.includes('succeeded')) {
      return 'bg-green-500/10 text-green-600 border-green-500/20';
    }
    if (eventType.includes('failed') || eventType.includes('canceled')) {
      return 'bg-red-500/10 text-red-600 border-red-500/20';
    }
    return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1C1917] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw size={32} className="text-[#881337] animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading Stripe events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1C1917]">
      {/* Header */}
      <div className="bg-[#292524] border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="text-white/60 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <Heart className="text-[#881337]" size={24} fill="#881337" />
              <span className="text-xl font-serif text-white">Stripe Events</span>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-[#881337] text-white text-sm font-medium hover:bg-[#6F0F2D] transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#292524] border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Activity size={20} className="text-blue-400" />
              <span className="text-white/60 text-sm">Total Events</span>
            </div>
            <p className="text-3xl font-serif text-white">{stats.total_events}</p>
          </div>
          
          <div className="bg-[#292524] border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle size={20} className="text-green-400" />
              <span className="text-white/60 text-sm">Successful Payments</span>
            </div>
            <p className="text-3xl font-serif text-green-400">{stats.successful_payments}</p>
          </div>
          
          <div className="bg-[#292524] border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-2">
              <XCircle size={20} className="text-red-400" />
              <span className="text-white/60 text-sm">Failed Payments</span>
            </div>
            <p className="text-3xl font-serif text-red-400">{stats.failed_payments}</p>
          </div>
          
          <div className="bg-[#292524] border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign size={20} className="text-[#881337]" />
              <span className="text-white/60 text-sm">Insight Revenue</span>
            </div>
            <p className="text-3xl font-serif text-white">${summary?.total_insight_revenue || 0}</p>
          </div>
        </div>

        {/* Recent Paid Purchases */}
        {summary?.recent_insight_payments?.length > 0 && (
          <div className="bg-[#292524] border border-white/10 p-6 mb-8">
            <h3 className="text-lg font-medium text-white mb-4">Recent Wedding Insight Purchases</h3>
            <div className="space-y-3">
              {summary.recent_insight_payments.map((payment) => (
                <div key={payment.id} className="p-4 bg-[#1C1917] border border-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-white font-medium">{payment.name || 'No name'}</p>
                      <p className="text-white/50 text-sm">{payment.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-medium">${payment.amount_paid}</p>
                      <p className="text-white/40 text-xs">{formatTimestamp(payment.paid_at)}</p>
                    </div>
                  </div>
                  {/* PaymentIntent ID - prominent display for cross-checking */}
                  {payment.payment_intent && (
                    <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between">
                      <span className="text-white/40 text-xs">PaymentIntent:</span>
                      <code className="text-green-400/80 text-xs font-mono bg-green-500/10 px-2 py-0.5">
                        {payment.payment_intent}
                      </code>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Filter by event type (e.g., checkout, payment_intent, charge)"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full md:w-96 px-4 py-3 bg-[#292524] border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-[#881337]"
          />
        </div>

        {/* Events Table */}
        <div className="bg-[#292524] border border-white/10 overflow-hidden">
          <div className="px-6 py-4 border-b border-white/10">
            <h3 className="text-lg font-medium text-white">Stripe Event Log</h3>
            <p className="text-white/50 text-sm">Real-time log of all Stripe webhook events</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="px-6 py-3 text-xs font-medium text-white/60 uppercase tracking-wider">Timestamp</th>
                  <th className="px-6 py-3 text-xs font-medium text-white/60 uppercase tracking-wider">Event Type</th>
                  <th className="px-6 py-3 text-xs font-medium text-white/60 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-xs font-medium text-white/60 uppercase tracking-wider">Payment Intent</th>
                  <th className="px-6 py-3 text-xs font-medium text-white/60 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-xs font-medium text-white/60 uppercase tracking-wider">Email</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {events.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-white/40">
                      No Stripe events logged yet. Events will appear here as they occur.
                    </td>
                  </tr>
                ) : (
                  events.map((event) => (
                    <tr key={event.id} className="hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4 text-white/70 text-sm whitespace-nowrap">
                        {formatTimestamp(event.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border ${getEventBadgeColor(event.event_type)}`}>
                          {getEventIcon(event.event_type)}
                          {event.event_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-white/70 text-sm">
                        {event.data?.status || event.data?.payment_status || '-'}
                      </td>
                      <td className="px-6 py-4 text-white/50 text-xs font-mono">
                        {event.data?.payment_intent || event.data?.payment_intent_id || '-'}
                      </td>
                      <td className="px-6 py-4 text-white/70 text-sm">
                        {event.data?.amount_total ? `$${(event.data.amount_total / 100).toFixed(2)}` : 
                         event.data?.amount ? `$${(event.data.amount / 100).toFixed(2)}` : '-'}
                      </td>
                      <td className="px-6 py-4 text-white/70 text-sm">
                        {event.data?.customer_email || event.data?.receipt_email || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Event Type Summary */}
        {summary?.event_counts && Object.keys(summary.event_counts).length > 0 && (
          <div className="mt-8 bg-[#292524] border border-white/10 p-6">
            <h3 className="text-lg font-medium text-white mb-4">Events by Type</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(summary.event_counts).map(([type, count]) => (
                <div key={type} className="p-3 bg-[#1C1917] border border-white/5">
                  <p className="text-white/50 text-xs truncate" title={type}>{type}</p>
                  <p className="text-white text-lg font-medium">{count}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStripeEvents;
